<script setup lang="ts">
	import { ref } from "vue";
	import CustomPopup from "~/components/customPopup.vue";
	import { z } from "~/utils/.fa-zod";
	import { useAuthStore } from "~/store/auth";
	import { toFarsiNumber } from "~/utils";

	const { $client } = useNuxtApp();
	const authStore = useAuthStore();
	const numberOfDigits = 4;

	const pinCodeInput = reactive({
		code: [] as string[],
		codeError: undefined as undefined | string,
		otpTimer: 0,
		otpTimerInterval: undefined as undefined | NodeJS.Timeout,
		popupType: "normal" as "normal" | "set" | "otp",
	});
	const isSubmitting = ref(false);
	const { getError, isValid, validate } = useValidation(
		z.object({
			code: z
				.string({
					errorMap: () => ({
						message: `کد تایید باید ${numberOfDigits} رقمی باشد`,
					}),
				})
				.array()
				.transform((val) => val.join(""))
				.superRefine((val, ctx) => {
					if (val.length < numberOfDigits) {
						ctx.addIssue({
							code: z.ZodIssueCode.too_small,
							minimum: numberOfDigits,
							type: "string",
							inclusive: true,
							message: `کد تایید باید ${numberOfDigits} رقمی باشد`,
						});
					}
					console.log(val, !Number(val));
					if (!Number(val)) {
						ctx.addIssue({
							code: z.ZodIssueCode.custom,
							message: "کد باید عدد باشد",
						});
					}
				}),
		}),
		pinCodeInput,
		{
			mode: "lazy",
		},
	);

	const popup = ref();
	const popupPinCode = ref();

	const setPin = (_: () => void) => {
		pinCodeInput.code = ["", "", "", ""];
		pinCodeInput.codeError = "";
		popupPinCode.value.title = "قفل کودک";
		popupPinCode.value.actionText = "تایید";
		popupPinCode.value.showClose = false;
		popupPinCode.value.closeOnAction = false;
		pinCodeInput.popupType = "set";
		popupPinCode.value.onClose = async () => {
			// cb();
		};
		popupPinCode.value.onAction = async () => {
			if (isSubmitting.value) return;
			isSubmitting.value = true;
			await validate();
			if (isValid.value) {
				popup.value.title = "تایید قفل کودک";
				popup.value.actionText = "تایید";
				popup.value.text = `قفل کودک شما ${pinCodeInput.code.join("")} است. آیا از انتخاب خود مطمئن هستید؟`;
				popup.value.showClose = false;
				popup.value.onBack = () => {
					popup.value.show = false;
					popupPinCode.value.show = true;
				};
				popup.value.onAction = async () => {
					try {
						const { success } =
							await $client.rProfile.setPincode.mutate({
								code: pinCodeInput.code.join(""),
							});
						if (!success) throw new Error();
						authStore.setPin(pinCodeInput.code.join(""));
						if (authStore.user) {
							authStore.user.pin = pinCodeInput.code.join("");
						}
						authStore.lockPin();
					} catch (_) {
						popup.value.title = "خطا";
						popup.value.actionText = "مشکلی پیش آمد";
						popup.value.showClose = true;
						popup.value.onBack = undefined;
						popup.value.onAction = undefined;
					}
					// cb();
				};
				popupPinCode.value.show = false;
				popup.value.show = true;
			}
			isSubmitting.value = false;
		};
		popupPinCode.value.show = true;
	};

	const checkPin = (_: () => void) => {
		pinCodeInput.code = ["", "", "", ""];
		pinCodeInput.codeError = "";
		popupPinCode.value.title = "قفل کودک";
		popupPinCode.value.actionText = "تایید";
		popupPinCode.value.closeOnAction = false;
		popupPinCode.value.showClose = false;
		pinCodeInput.popupType = "normal";
		popupPinCode.value.onAction = async () => {
			try {
				await validate();
				if (isValid.value) {
					const success = authStore.verifyPin(
						pinCodeInput.code.join(""),
					);
					console.log(success);
					if (success) {
						popupPinCode.value.show = false;
					} else {
						pinCodeInput.codeError = "کد اشتباه است.";
					}
				} else {
					popupPinCode.value.show = true;
				}
			} catch (_) {
				popup.value.title = "قفل کودک";
				popup.value.actionText = "کد وارد شده اشتباه است";
				popup.value.showClose = true;
				popup.value.onBack = undefined;
				popup.value.onAction = undefined;
			}
			// cb();
		};
		popupPinCode.value.show = true;
	};

	async function handleSendOtpAgain() {
		if (pinCodeInput.otpTimer > 0) return;
		if (isSubmitting.value) return;
		isSubmitting.value = true;
		const { success, data } =
			await $client.rProfile.changePincodeSendOtp.mutate({});
		isSubmitting.value = false;
		if (success && !!data.expire_time) {
			pinCodeInput.codeError = "";
			pinCodeInput.otpTimer = data.expire_time;
			clearInterval(pinCodeInput.otpTimerInterval);
			pinCodeInput.otpTimerInterval = setInterval(() => {
				if (pinCodeInput.otpTimer < 1) {
					clearInterval(pinCodeInput.otpTimerInterval);
				} else {
					pinCodeInput.otpTimer -= 1;
				}
			}, 1000);
		}
	}

	const forgetPin = async (cb: () => void = () => {}) => {
		if (isSubmitting.value) return;
		isSubmitting.value = true;
		const { success, data } =
			await $client.rProfile.changePincodeSendOtp.mutate({});
		isSubmitting.value = false;
		pinCodeInput.code = ["", "", "", ""];
		pinCodeInput.codeError = "";
		if (success && !!data.expire_time) {
			pinCodeInput.codeError = "";
			pinCodeInput.otpTimer = data.expire_time;
			pinCodeInput.otpTimerInterval = setInterval(() => {
				if (pinCodeInput.otpTimer < 1) {
					clearInterval(pinCodeInput.otpTimerInterval);
				} else {
					pinCodeInput.otpTimer -= 1;
				}
			}, 1000);

			popupPinCode.value.title = "فراموشی قفل کودک";
			popupPinCode.value.actionText = "ادامه";
			popupPinCode.value.showClose = false;
			popupPinCode.value.closeOnAction = false;
			pinCodeInput.popupType = "otp";
			popupPinCode.value.onClose = async () => {
				clearInterval(pinCodeInput.otpTimerInterval);
			};
			popupPinCode.value.onAction = async () => {
				try {
					if (isSubmitting.value) return;
					isSubmitting.value = true;
					await validate();
					if (isValid.value) {
						const res =
							await $client.rProfile.changePincodeOtpCheck.mutate(
								{
									otp: pinCodeInput.code.join(""),
								},
							);
						if (!res) {
							pinCodeInput.codeError = "کد وارد شده اشتباه است";
							popupPinCode.value.show = true;
						} else {
							clearInterval(pinCodeInput.otpTimerInterval);
							popupPinCode.value.show = false;
							setPin(cb);
						}
					} else {
						popupPinCode.value.show = true;
					}
				} catch (e: unknown) {
					popup.value.title = "قفل کودک";
					popup.value.text = "مشکلی پیش آمد.";
					if (
						e instanceof Error &&
						e!.data!.code === "FORBIDDEN" &&
						e.message
					) {
						popup.value.text = e.message;
					}
					popup.value.onAction = undefined;
					popup.value.showClose = true;
					popup.value.onClose = () => {
						// cb();
					};
					popupPinCode.value.show = false;
					popup.value.show = true;
				}
				isSubmitting.value = false;
			};
			popupPinCode.value.show = true;
		} else {
			pinCodeInput.codeError = "مشکل ارتباط با سامانه sms";
		}
	};

	defineExpose({
		setPin,
		checkPin,
		forgetPin,
	});
</script>

<template>
	<div>
		<CustomPopup ref="popup" />
		<CustomPopup ref="popupPinCode">
			<template #body>
				<form
					class="flex flex-col mt-4 border-t-[1px] border-gray-300 py-6 gap-y-4"
					@submit.prevent
				>
					<p v-if="pinCodeInput.popupType === 'normal'">
						لطفا قفل کودک خود را وارد نمایید
					</p>
					<p v-else-if="pinCodeInput.popupType === 'set'">
						لطفا قفل کودک خود را تعیین نمایید
					</p>
					<p v-else-if="pinCodeInput.popupType === 'otp'">
						کد ۴ رقمی به شماره
						{{ toFarsiNumber(authStore?.user?.phone_number) }} ارسال
						شد
					</p>
					<InputsInputOtp
						v-model="pinCodeInput.code"
						:error="pinCodeInput.codeError || getError('code')"
						:count="4"
					/>
					<div
						v-if="pinCodeInput.popupType === 'normal'"
						class="w-full flex flex-row justify-between"
					>
						<div
							class="text-primary cursor-pointer hover:text-primaryHover"
							@click="forgetPin"
						>
							فراموشی؟
						</div>
						<div
							class="text-primary cursor-pointer hover:text-primaryHover"
							@click="forgetPin"
						>
							تغییر؟
						</div>
					</div>
					<div
						v-if="pinCodeInput.popupType === 'otp'"
						class="flex justify-center items-center w-full"
					>
						<span
							v-if="pinCodeInput.otpTimer != 0"
							class="text-btnGrey h-btn flex justify-center items-center"
						>
							{{
								toFarsiNumber(formatTime(pinCodeInput.otpTimer))
							}}
							تا ارسال مجدد کد</span
						>
						<button
							v-if="pinCodeInput.otpTimer == 0"
							type="button"
							class="h-btn outline-black rounded-full hover:text-primaryHover flex cursor-pointer w-full justify-center items-center text-primary text-sm font-medium border-transparent transition-colors"
							@click="handleSendOtpAgain"
						>
							ارسال مجدد کد
						</button>
					</div>
					<span
						v-if="pinCodeInput.codeError"
						class="label-text-alt text-errorRed"
					>
						-{{ pinCodeInput.codeError }}
					</span>
					<span
						v-if="getError('code')"
						class="label-text-alt text-errorRed"
					>
						-{{ getError("code") }}
					</span>
				</form>
			</template>
		</CustomPopup>
	</div>
</template>

<style scoped></style>
